import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { suppliesService, warehousesService, movementTypesService } from '../../services/productionService';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import TableComponent from '../../components/TableComponent';
import Drawer from '../../components/Drawer';
import ConfirmDialog from '../../components/ConfirmDialog';
import usePageTitle from '../../utils/usePageTitle';
import { useToast } from '../../contexts/ToastContext';
import { formatDateBR, formatQuantity, formatSignedQuantity, localDateISO } from '../../lib/utils';

/** Legado: tipo em `stock_movements.type` quando sem catálogo */
const STOCK_TYPE_LABELS = {
    production_report: 'Produção',
    inventory_in: 'Entrada inventário',
    order_out: 'Saída de pedido',
    invoice_out: 'Saída de NF',
    inventory_out: 'Saída de inventário',
    scrap: 'Refugo',
    second_line: '2ª linha',
    adjustment: 'Ajuste (legado)',
};

const INCREMENT_TYPES = ['production_report', 'inventory_in'];

function SupplyForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;
    usePageTitle(isEditing ? 'Editar Insumo' : 'Novo Insumo');
    const { toast, toastApiError } = useToast();
    
    const [supply, setSupply] = useState(null);
    const [loading, setLoading] = useState(true);
    const [formLoading, setFormLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('general');
    const [costHistory, setCostHistory] = useState([]);
    const [costHistoryLoading, setCostHistoryLoading] = useState(false);
    const [costDrawerOpen, setCostDrawerOpen] = useState(false);
    const [costLoading, setCostLoading] = useState(false);
    const [costFormData, setCostFormData] = useState({
        new_cost: '',
        notes: '',
        entry_date: localDateISO(),
    });
    const [costEditingId, setCostEditingId] = useState(null);
    const [costDeleteOpen, setCostDeleteOpen] = useState(false);
    const [costRowPendingDelete, setCostRowPendingDelete] = useState(null);

    const [movementTypesCatalog, setMovementTypesCatalog] = useState([]);
    const [defaultStockMovementTypeId, setDefaultStockMovementTypeId] = useState(null);
    const [warehouses, setWarehouses] = useState([]);
    const [stockMovements, setStockMovements] = useState([]);
    const [stockMovementsLoading, setStockMovementsLoading] = useState(false);
    const [adjustmentDrawerOpen, setAdjustmentDrawerOpen] = useState(false);
    const [adjustmentLoading, setAdjustmentLoading] = useState(false);
    const [adjustmentFormData, setAdjustmentFormData] = useState({
        warehouse_id: '',
        quantity: '',
        movement_type_id: '',
        movement_date: localDateISO(),
        document: '',
        empenho: '',
        notes: '',
    });

    const [formData, setFormData] = useState({
        code: '',
        description: '',
        unit_of_measure: '',
        unit_cost: '',
        status: 'active',
        min_stock: '0',
        max_stock: '0',
        default_warehouse_id: '',
    });

    useEffect(() => {
        loadWarehouses();
        let cancelled = false;
        (async () => {
            try {
                const response = await movementTypesService.list({ per_page: 500 });
                const rows = response.data?.data || response.data || [];
                const arr = Array.isArray(rows) ? rows : [];
                if (!cancelled) {
                    setMovementTypesCatalog(arr);
                    const ie = arr.find((t) => String(t.code || '').toUpperCase() === 'IE');
                    setDefaultStockMovementTypeId(ie?.id ?? arr.find((t) => t.direction === 'in')?.id ?? null);
                }
            } catch {
                /* ignorar */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (isEditing) {
            loadSupply();
            loadCostHistory();
            loadStockMovements();
        } else {
            setLoading(false);
        }
    }, [id, isEditing]);

    const loadSupply = async () => {
        setLoading(true);
        try {
            const response = await suppliesService.get(id);
            const data = response.data.data || response.data;
            setSupply(data);
            setFormData({
                code: data.code || '',
                description: data.description || '',
                unit_of_measure: data.unit_of_measure || '',
                unit_cost: data.unit_cost || '',
                status: data.status || 'active',
                min_stock: data.min_stock != null && data.min_stock !== '' ? String(data.min_stock) : '0',
                max_stock: data.max_stock != null && data.max_stock !== '' ? String(data.max_stock) : '0',
                default_warehouse_id:
                    data.default_warehouse_id != null ? String(data.default_warehouse_id) : '',
            });
        } catch (error) {
            console.error('Error loading supply:', error);
            toast({
                title: 'Erro ao carregar',
                description: 'Não foi possível carregar o insumo',
                variant: 'error',
            });
            navigate('/production/supplies');
        } finally {
            setLoading(false);
        }
    };

    const loadWarehouses = async () => {
        try {
            const response = await warehousesService.list();
            setWarehouses(response.data.data || response.data);
        } catch (error) {
            console.error('Error loading warehouses:', error);
        }
    };

    const loadStockMovements = async () => {
        if (!id) return;

        setStockMovementsLoading(true);
        try {
            const response = await suppliesService.getStockMovements(id);
            const data = response.data.data || response.data;
            setStockMovements(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading stock movements:', error);
            setStockMovements([]);
        } finally {
            setStockMovementsLoading(false);
        }
    };

    const resolveDefaultWarehouseId = () => {
        if (formData.default_warehouse_id !== '' && formData.default_warehouse_id != null) {
            return formData.default_warehouse_id;
        }
        if (supply?.default_warehouse_id != null) {
            return supply.default_warehouse_id;
        }
        return null;
    };

    const buildFreshAdjustmentForm = () => {
        const defaultWarehouseId = resolveDefaultWarehouseId();
        return {
            warehouse_id: defaultWarehouseId != null ? String(defaultWarehouseId) : '',
            quantity: '',
            movement_type_id: defaultStockMovementTypeId != null ? String(defaultStockMovementTypeId) : '',
            movement_date: localDateISO(),
            document: '',
            empenho: '',
            notes: '',
        };
    };

    const openStockAdjustmentDrawer = () => {
        setAdjustmentFormData(buildFreshAdjustmentForm());
        setAdjustmentDrawerOpen(true);
    };

    const handleAdjustmentFormChange = (e) => {
        setAdjustmentFormData({
            ...adjustmentFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleAdjustmentSubmit = async (e) => {
        e.preventDefault();
        const qty = parseFloat(String(adjustmentFormData.quantity).replace(',', '.'));
        if (!Number.isFinite(qty) || qty <= 0) {
            toast({
                title: 'Quantidade inválida',
                description: 'Informe uma quantidade maior que zero.',
                variant: 'error',
            });
            return;
        }
        const mid = adjustmentFormData.movement_type_id
            ? parseInt(adjustmentFormData.movement_type_id, 10)
            : null;
        if (!mid || Number.isNaN(mid)) {
            toast({
                title: 'Tipo obrigatório',
                description: 'Selecione um tipo de movimentação cadastrado.',
                variant: 'error',
            });
            return;
        }
        setAdjustmentLoading(true);
        try {
            await suppliesService.adjustInventory(id, {
                warehouse_id: parseInt(adjustmentFormData.warehouse_id, 10),
                movement_type_id: mid,
                quantity: qty,
                movement_date: adjustmentFormData.movement_date,
                document: adjustmentFormData.document?.trim() || null,
                empenho: adjustmentFormData.empenho?.trim() || null,
                notes: adjustmentFormData.notes?.trim() || null,
            });
            setAdjustmentFormData(buildFreshAdjustmentForm());
            setAdjustmentDrawerOpen(false);
            await loadStockMovements();
            toast({
                title: 'Movimentação registrada',
                description: 'Estoque do insumo atualizado.',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error adjusting supply stock:', error);
            toastApiError(error, { title: 'Erro', fallback: 'Não foi possível registrar a movimentação.' });
        } finally {
            setAdjustmentLoading(false);
        }
    };

    const getMovementTypeDisplay = (row) => {
        const label = row.type_label || STOCK_TYPE_LABELS[row.type] || row.type || '-';
        const dir = row.movement_type?.direction;
        const inc =
            dir === 'in'
                ? true
                : dir === 'out'
                  ? false
                  : INCREMENT_TYPES.includes(row.type);
        const cls = inc ? 'bg-emerald-100 text-emerald-900'
            : row.type === 'adjustment' ? 'bg-amber-100 text-amber-900'
                : 'bg-rose-100 text-rose-900';
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${cls}`}>{label}</span>
        );
    };

    const handleFormChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const loadCostHistory = async () => {
        if (!id) return;
        
        setCostHistoryLoading(true);
        try {
            const response = await suppliesService.getCostHistory(id);
            const raw = response.data;
            const data = Array.isArray(raw) ? raw : (raw?.data ?? []);
            setCostHistory(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading cost history:', error);
            setCostHistory([]);
        } finally {
            setCostHistoryLoading(false);
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormLoading(true);

        try {
            const payload = {
                code: formData.code,
                description: formData.description,
                unit_of_measure: formData.unit_of_measure,
                unit_cost: formData.unit_cost,
                status: formData.status,
                min_stock: parseFloat(String(formData.min_stock).replace(',', '.')) || 0,
                max_stock: parseFloat(String(formData.max_stock).replace(',', '.')) || 0,
                default_warehouse_id:
                    formData.default_warehouse_id !== '' && formData.default_warehouse_id != null
                        ? parseInt(String(formData.default_warehouse_id), 10)
                        : null,
            };

            if (isEditing) {
                const response = await suppliesService.update(id, payload);
                const saved = response.data?.data || response.data;
                if (saved) {
                    setSupply(saved);
                    setFormData((prev) => ({
                        ...prev,
                        default_warehouse_id:
                            saved.default_warehouse_id != null ? String(saved.default_warehouse_id) : '',
                    }));
                }
            } else {
                const response = await suppliesService.create(payload);
                const saved = response.data?.data || response.data;
                if (saved) {
                    setSupply(saved);
                    setFormData((prev) => ({
                        ...prev,
                        default_warehouse_id:
                            saved.default_warehouse_id != null ? String(saved.default_warehouse_id) : '',
                    }));
                }
            }
            toast({
                title: 'Insumo salvo',
                description: 'Alterações salvas com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error saving supply:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar o insumo' });
        } finally {
            setFormLoading(false);
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    };

    const formatLaunchDate = (row) => {
        const raw = row.entry_date ?? row.created_at;
        if (!raw) return '-';
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) {
            const [y, m, d] = raw.slice(0, 10).split('-');
            return `${d}/${m}/${y}`;
        }
        const date = new Date(raw);
        if (Number.isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const launchDateFromRow = (row) => {
        const raw = row.entry_date ?? row.created_at;
        if (!raw) return localDateISO();
        if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
        try {
            return localDateISO(new Date(raw));
        } catch {
            return localDateISO();
        }
    };

    const handleCostFormChange = (e) => {
        setCostFormData({
            ...costFormData,
            [e.target.name]: e.target.value,
        });
    };

    const handleCostSubmit = async (e) => {
        e.preventDefault();
        setCostLoading(true);
        try {
            const newCost = parseFloat(String(costFormData.new_cost).replace(',', '.')) || 0;
            const payload = {
                entry_date: costFormData.entry_date,
                new_cost: newCost,
                notes: costFormData.notes?.trim() ? costFormData.notes : null,
            };

            if (costEditingId) {
                await suppliesService.patchCostHistory(id, costEditingId, payload);
            } else {
                await suppliesService.updateCost(id, payload);
            }

            await loadSupply();
            await loadCostHistory();

            setCostDrawerOpen(false);
            setCostEditingId(null);
            setCostFormData({
                new_cost: '',
                notes: '',
                entry_date: localDateISO(),
            });
            toast({
                title: 'Custo atualizado',
                description: 'Alterações registradas no histórico',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error updating cost:', error);
            toastApiError(error, { title: 'Erro ao atualizar custo', fallback: 'Não foi possível atualizar o custo' });
        } finally {
            setCostLoading(false);
        }
    };

    const handleConfirmDeleteCost = async () => {
        if (!costRowPendingDelete?.id) return;
        setCostLoading(true);
        try {
            await suppliesService.deleteCostHistory(id, costRowPendingDelete.id);
            await loadSupply();
            await loadCostHistory();
            toast({
                title: 'Lançamento excluído',
                description: 'O histórico de custo foi atualizado.',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error deleting cost history:', error);
            toastApiError(error, { title: 'Erro ao excluir', fallback: 'Não foi possível excluir o lançamento.' });
        } finally {
            setCostLoading(false);
            setCostDeleteOpen(false);
            setCostRowPendingDelete(null);
        }
    };

    return (
        <AppLayout>
            <div className="space-y-6">
                {/* Header */}
                <PageHeader
                    title={isEditing ? `Editar Insumo - ${supply?.code ?? ''}` : 'Novo Insumo'}
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Produção', path: '/production/supplies' },
                        { label: 'Insumos', path: '/production/supplies' },
                    ]}
                    actions={[
                        {
                            label: 'Voltar',
                            icon: 'back',
                            variant: 'outline',
                            as: Link,
                            to: '/production/supplies',
                        },
                        {
                            label: formLoading ? 'Salvando...' : 'Salvar',
                            icon: 'save',
                            onClick: () => {
                                const form = document.getElementById('supply-form');
                                if (form) {
                                    form.requestSubmit();
                                }
                            },
                            disabled: formLoading || loading,
                        },
                    ]}
                />

                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="general" activeValue={activeTab} onValueChange={setActiveTab}>
                            Geral
                        </TabsTrigger>
                        {isEditing && (
                            <TabsTrigger value="cost-history" activeValue={activeTab} onValueChange={setActiveTab}>
                                Custo
                            </TabsTrigger>
                        )}
                        {isEditing && (
                            <TabsTrigger value="stock" activeValue={activeTab} onValueChange={setActiveTab}>
                                Estoque
                            </TabsTrigger>
                        )}
                    </TabsList>

                    {/* Tab: Geral */}
                    <TabsContent value="general" activeValue={activeTab} className="mt-6">
                        <Card>
                            <CardHeader>
                                <CardTitle>Informações do Insumo</CardTitle>
                            </CardHeader>
                            <CardContent>
                        <form id="supply-form" onSubmit={handleFormSubmit} className="space-y-6" autoComplete="off">
                            {/* Seção: Identificação */}
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                        </svg>
                                    </div>
                                    <h3 className="text-md font-semibold text-gray-900">
                                        Identificação
                                    </h3>
                                </div>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Código *
                                            </label>
                                            <Input
                                                name="code"
                                                value={formData.code}
                                                onChange={handleFormChange}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Unidade de Medida *
                                            </label>
                                            <Input
                                                name="unit_of_measure"
                                                value={formData.unit_of_measure}
                                                onChange={handleFormChange}
                                                placeholder="kg, L, un, etc"
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Descrição *
                                        </label>
                                        <Input
                                            name="description"
                                            value={formData.description}
                                            onChange={handleFormChange}
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Seção: Status */}
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="text-md font-semibold text-gray-900">
                                        Status
                                    </h3>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status *
                                    </label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleFormChange}
                                        className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        required
                                    >
                                        <option value="active">Ativo</option>
                                        <option value="inactive">Inativo</option>
                                    </select>
                                </div>
                            </div>

                            {/* Limites para referência nos cartões da aba Estoque */}
                            <div>
                                <div className="flex items-center gap-2 mb-4">
                                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V7a2 2 0 00-2-2H7l-5 5v11a2 2 0 002 2h5" />
                                        </svg>
                                    </div>
                                    <h3 className="text-md font-semibold text-gray-900">
                                        Referência de estoque
                                    </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Estoque mínimo
                                        </label>
                                        <Input
                                            type="number"
                                            name="min_stock"
                                            step="0.001"
                                            min="0"
                                            value={formData.min_stock}
                                            onChange={handleFormChange}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Estoque máximo
                                        </label>
                                        <Input
                                            type="number"
                                            name="max_stock"
                                            step="0.001"
                                            min="0"
                                            value={formData.max_stock}
                                            onChange={handleFormChange}
                                        />
                                    </div>
                                </div>
                                <p className="mt-2 text-xs text-gray-500">Os valores são orientativos; o saldo real vem das movimentações na aba Estoque.</p>
                                <div className="mt-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Depósito padrão
                                    </label>
                                    <select
                                        name="default_warehouse_id"
                                        value={formData.default_warehouse_id}
                                        onChange={handleFormChange}
                                        className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Nenhum</option>
                                        {warehouses.map((w) => (
                                            <option key={w.id} value={w.id}>
                                                {w.name}
                                            </option>
                                        ))}
                                    </select>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Sugerido automaticamente nas movimentações de estoque deste insumo.
                                    </p>
                                </div>
                            </div>
                        </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    {/* Tab: Custo - apenas se estiver editando */}
                    {isEditing && (
                        <TabsContent value="cost-history" activeValue={activeTab} className="mt-6">
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Histórico de Alteração de Custos</CardTitle>
                                        <Button onClick={() => {
                                            setCostEditingId(null);
                                            setCostFormData({
                                                new_cost: formData.unit_cost || '',
                                                notes: '',
                                                entry_date: localDateISO(),
                                            });
                                            setCostDrawerOpen(true);
                                        }}>
                                            Alterar Custo
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {costHistoryLoading && costHistory.length === 0 ? null : (
                                        <TableComponent
                                            columns={[
                                                {
                                                    key: 'entry_date',
                                                    label: 'Data do lançamento',
                                                    render: (_value, row) => formatLaunchDate(row),
                                                },
                                                {
                                                    key: 'old_cost',
                                                    label: 'Custo Anterior',
                                                    render: (value) => formatCurrency(value),
                                                },
                                                {
                                                    key: 'new_cost',
                                                    label: 'Custo Novo',
                                                    icon: 'new',
                                                    render: (value) => (
                                                        <span className="font-semibold text-green-600">
                                                            {formatCurrency(value)}
                                                        </span>
                                                    ),
                                                },
                                                {
                                                    key: 'difference',
                                                    label: 'Variação',
                                                    render: (value, row) => {
                                                        const diff = parseFloat(value || row.new_cost - row.old_cost || 0);
                                                        const diffPercent = row.old_cost > 0 
                                                            ? ((diff / row.old_cost) * 100).toFixed(2)
                                                            : '0.00';
                                                        return (
                                                            <div className="flex flex-col">
                                                                <span className={diff >= 0 ? 'text-red-600' : 'text-green-600'}>
                                                                    {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
                                                                </span>
                                                                <span className={`text-xs ${diff >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                                                                    ({diff >= 0 ? '+' : ''}{diffPercent}%)
                                                                </span>
                                                            </div>
                                                        );
                                                    },
                                                },
                                                {
                                                    key: 'user',
                                                    label: 'Usuário',
                                                    render: (value, row) => row.user?.name || '-',
                                                },
                                                {
                                                    key: 'notes',
                                                    label: 'Observações',
                                                    render: (value) => value || '-',
                                                },
                                            ]}
                                            data={costHistory}
                                            emptyMessage="Nenhuma alteração de custo registrada"
                                            pagination={false}
                                            onEdit={(row) => {
                                                setCostEditingId(row.id);
                                                setCostFormData({
                                                    new_cost: row.new_cost != null ? String(row.new_cost) : '',
                                                    notes: row.notes || '',
                                                    entry_date: launchDateFromRow(row),
                                                });
                                                setCostDrawerOpen(true);
                                            }}
                                            onDelete={(row) => {
                                                setCostRowPendingDelete(row);
                                                setCostDeleteOpen(true);
                                            }}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                    {isEditing && (
                        <TabsContent value="stock" activeValue={activeTab} className="mt-6">
                            {(() => {
                                const currentBalance = stockMovements.reduce((sum, m) => {
                                    const qty = parseFloat(m.quantity);
                                    return sum + (Number.isFinite(qty) ? qty : 0);
                                }, 0);
                                const minStock = parseFloat(String(formData.min_stock).replace(',', '.')) || 0;
                                const maxStock = parseFloat(String(formData.max_stock).replace(',', '.')) || 0;
                                const balanceByWarehouse = Object.values(
                                    stockMovements.reduce((acc, m) => {
                                        const key = m.warehouse_id ?? 'none';
                                        const qty = parseFloat(m.quantity);
                                        acc[key] = acc[key] || { id: key, name: m.warehouse?.name || '-', balance: 0 };
                                        acc[key].balance += Number.isFinite(qty) ? qty : 0;
                                        return acc;
                                    }, {}),
                                ).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));

                                return (
                                    <>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <p className="text-sm text-gray-600">Mínimo</p>
                                            <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(minStock)}</p>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <p className="text-sm text-gray-600">Máximo</p>
                                            <p className="text-2xl font-bold mt-1 text-gray-900">{formatQuantity(maxStock)}</p>
                                        </div>
                                        <div className="bg-white p-4 rounded-lg border border-gray-200">
                                            <p className="text-sm text-gray-600">Saldo atual</p>
                                            <p
                                                className={`text-2xl font-bold mt-1 ${
                                                    currentBalance < minStock ? 'text-red-600'
                                                        : currentBalance > maxStock ? 'text-yellow-600'
                                                            : 'text-gray-900'
                                                }`}
                                            >
                                                {formatQuantity(currentBalance)}
                                            </p>
                                        </div>
                                    </div>
                                    {balanceByWarehouse.length > 0 && (
                                        <Card className="mb-6">
                                            <CardHeader>
                                                <CardTitle>Saldo por depósito</CardTitle>
                                            </CardHeader>
                                            <CardContent>
                                                <TableComponent
                                                    columns={[
                                                        { key: 'name', label: 'Depósito', className: 'whitespace-nowrap' },
                                                        {
                                                            key: 'balance',
                                                            label: 'Saldo',
                                                            align: 'right',
                                                            className: 'text-right tabular-nums',
                                                            render: (value) => formatQuantity(value),
                                                        },
                                                    ]}
                                                    data={balanceByWarehouse}
                                                />
                                            </CardContent>
                                        </Card>
                                    )}
                                    </>
                                );
                            })()}
                            <Card>
                                <CardHeader>
                                    <div className="flex items-center justify-between">
                                        <CardTitle>Movimentação de Estoque</CardTitle>
                                        <Button type="button" onClick={openStockAdjustmentDrawer}>
                                            Nova movimentação
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    {stockMovementsLoading && stockMovements.length === 0 ? null : (
                                        <TableComponent
                                            columns={[
                                                {
                                                    key: 'date',
                                                    label: 'Data',
                                                    render: (value) => formatDateBR(value),
                                                },
                                                {
                                                    key: 'warehouse',
                                                    label: 'Depósito',
                                                    className: 'whitespace-nowrap',
                                                    render: (value, row) => row.warehouse?.name || '-',
                                                },
                                                {
                                                    key: 'type',
                                                    label: 'Tipo',
                                                    render: (value, row) => getMovementTypeDisplay(row),
                                                },
                                                {
                                                    key: 'document',
                                                    label: 'Documento',
                                                    render: (value, row) => {
                                                        const v = row.document ?? value;
                                                        const s =
                                                            v != null && String(v).trim() !== '' ? String(v) : '';
                                                        if (!s) return '-';
                                                        return <span className="whitespace-pre-line">{s}</span>;
                                                    },
                                                },
                                                {
                                                    key: 'quantity',
                                                    label: 'Quantidade',
                                                    render: (value) => formatSignedQuantity(value),
                                                },
                                                {
                                                    key: 'empenho',
                                                    label: 'Empenho',
                                                    render: (value) =>
                                                        value != null && String(value).trim() !== '' ? String(value) : '-',
                                                },
                                                {
                                                    key: 'notes',
                                                    label: 'Observações',
                                                    render: (value) =>
                                                        value != null && String(value).trim() !== '' ? (
                                                            <span className="line-clamp-2" title={value}>{String(value)}</span>
                                                        ) : '-',
                                                },
                                                {
                                                    key: 'origin',
                                                    label: 'Origem',
                                                    render: (value, row) => {
                                                        if (!row.origin) return <span className="text-gray-400">-</span>;
                                                        const originRef = row.origin.reference || '';
                                                        const originDesc = row.origin.description || '';
                                                        const oid =
                                                            row.origin.production_order_id ||
                                                            row.origin.productionOrderId ||
                                                            originRef.match(/#?(\d+)/)?.[1];
                                                        const isPo = String(originDesc).toLowerCase().includes('ordem')
                                                            || String(originRef).toLowerCase().includes('ordem');
                                                        return (
                                                            <div className="flex flex-col gap-0.5">
                                                                {isPo && oid ? (
                                                                    <Link
                                                                        to={`/production/orders/${oid}`}
                                                                        className="text-sm text-blue-600 hover:underline"
                                                                    >
                                                                        {originRef || `OP #${oid}`}
                                                                    </Link>
                                                                ) : (
                                                                    <span className="text-sm">{originRef || '-'}</span>
                                                                )}
                                                                {originDesc ? (
                                                                    <span className="text-xs text-gray-500">{originDesc}</span>
                                                                ) : null}
                                                            </div>
                                                        );
                                                    },
                                                },
                                                {
                                                    key: 'user',
                                                    label: 'Usuário',
                                                    render: (value, row) => row.user?.name || '-',
                                                },
                                            ]}
                                            data={stockMovements}
                                            emptyMessage="Nenhuma movimentação de estoque"
                                            pagination={false}
                                        />
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                    )}
                </Tabs>

                {/* Drawer de movimentação de estoque (insumo) */}
                <Drawer
                    open={adjustmentDrawerOpen}
                    onClose={() => {
                        setAdjustmentDrawerOpen(false);
                        setAdjustmentFormData(buildFreshAdjustmentForm());
                    }}
                    title="Movimentação de estoque"
                    size="md"
                    footer={
                        <div className="flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setAdjustmentDrawerOpen(false);
                                    setAdjustmentFormData(buildFreshAdjustmentForm());
                                }}
                                disabled={adjustmentLoading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                form="supply-adjustment-form"
                                disabled={adjustmentLoading}
                            >
                                {adjustmentLoading ? 'Salvando…' : 'Salvar'}
                            </Button>
                        </div>
                    }
                >
                    <form id="supply-adjustment-form" onSubmit={handleAdjustmentSubmit} className="space-y-4">
                        <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                            <span className="font-medium text-gray-900">Item: </span>
                            {formData.code ? `${formData.code} - ${formData.description || ''}` : '-'}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Data *</label>
                            <MaskedInput
                                mask="date"
                                name="movement_date"
                                value={adjustmentFormData.movement_date}
                                onChange={handleAdjustmentFormChange}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Depósito *</label>
                            <select
                                name="warehouse_id"
                                value={adjustmentFormData.warehouse_id}
                                onChange={handleAdjustmentFormChange}
                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                            >
                                <option value="">Selecione...</option>
                                {warehouses.map((w) => (
                                    <option key={w.id} value={w.id}>{w.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de movimento *</label>
                            <select
                                name="movement_type_id"
                                value={adjustmentFormData.movement_type_id}
                                onChange={handleAdjustmentFormChange}
                                className="flex h-10 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                required
                            >
                                <option value="">Selecione...</option>
                                {movementTypesCatalog.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {t.code} - {t.name} ({t.direction === 'in' ? 'entrada' : 'saída'})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
                            <Input type="number" step="0.001" name="quantity" value={adjustmentFormData.quantity} onChange={handleAdjustmentFormChange} required />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Documento</label>
                            <Input type="text" name="document" value={adjustmentFormData.document} onChange={handleAdjustmentFormChange} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Empenho</label>
                            <Input type="text" name="empenho" value={adjustmentFormData.empenho} onChange={handleAdjustmentFormChange} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                            <textarea name="notes" value={adjustmentFormData.notes} onChange={handleAdjustmentFormChange} rows={4} className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                        </div>
                    </form>
                </Drawer>

                {/* Drawer de Alteração de Custo */}
                <Drawer
                    open={costDrawerOpen}
                    onClose={() => {
                        setCostDrawerOpen(false);
                        setCostEditingId(null);
                        setCostFormData({
                            new_cost: '',
                            notes: '',
                            entry_date: localDateISO(),
                        });
                    }}
                    title={costEditingId ? 'Editar lançamento de custo' : 'Alterar Custo do Insumo'}
                    size="md"
                    footer={
                        <div className="flex justify-end gap-3">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setCostDrawerOpen(false);
                                    setCostEditingId(null);
                                    setCostFormData({
                                        new_cost: '',
                                        notes: '',
                                        entry_date: localDateISO(),
                                    });
                                }}
                                disabled={costLoading}
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" form="supply-cost-form" disabled={costLoading}>
                                {costLoading ? 'Salvando…' : 'Salvar'}
                            </Button>
                        </div>
                    }
                >
                    <form id="supply-cost-form" onSubmit={handleCostSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Data do lançamento *
                            </label>
                            <MaskedInput
                                mask="date"
                                name="entry_date"
                                value={costFormData.entry_date}
                                onChange={handleCostFormChange}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Custo Atual
                            </label>
                            <Input
                                type="text"
                                value={formatCurrency(formData.unit_cost || 0)}
                                disabled
                                className="bg-gray-50"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Novo Custo *
                            </label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                name="new_cost"
                                value={costFormData.new_cost}
                                onChange={handleCostFormChange}
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Observações
                            </label>
                            <textarea
                                name="notes"
                                value={costFormData.notes}
                                onChange={handleCostFormChange}
                                rows={4}
                                className="flex w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>

                    </form>
                </Drawer>

                <ConfirmDialog
                    open={costDeleteOpen}
                    title="Excluir lançamento?"
                    message="Esta ação remove o registro do histórico e recalcula o custo unitário atual conforme os demais lançamentos."
                    onConfirm={handleConfirmDeleteCost}
                    onCancel={() => {
                        setCostDeleteOpen(false);
                        setCostRowPendingDelete(null);
                    }}
                />
            </div>
        </AppLayout>
    );
}

export default SupplyForm;

